import { useCallback, useEffect, useState } from "react";
import { deleteModel, getModelById } from "services/DataModelService";
import ModelList from "modules/datamodel/components/ModelList";
import { MetadataTypeEnum } from "build-ts/enums";
import { DataModelStats } from "build-ts/interface";
import { useNavigate } from "react-router-dom";
import { fetchMetadataHeaders, HeaderWithStats } from "services/MetadataServices";
import showToast from "hooks/useCustomToast";

const ModelListContainer: React.FC = () => {
  const navigate = useNavigate();
  const [modelList, setModelList] = useState<HeaderWithStats[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      // Phase 1: load card headers quickly without stats
      const initial = await fetchMetadataHeaders(
        MetadataTypeEnum.DATA_MODEL,
        -1,
        1,
        "LAST_MODIFIED",
        false,
        "",
        "",
        [],
        false
      );
      if (initial) {
        setModelList(initial.headers as HeaderWithStats[]);
      }

      // Phase 2: enrich with stats for the progress ring and stat chips
      const withStats = await fetchMetadataHeaders(
        MetadataTypeEnum.DATA_MODEL,
        -1,
        1,
        "LAST_MODIFIED",
        false,
        "",
        "",
        [],
        true
      );
      if (withStats) {
        setModelList(withStats.headers as HeaderWithStats[]);
      }
      setStatsLoading(false);
    };

    fetchData();
  }, []);

  const handleOpenModelEditor = (id: string) => {
    navigate(`/mkg/models/${id}`);
  };

  const handleOpenThoughtspotFormula = useCallback(
    async (modelId: string) => {
      const modelInfo = await getModelById(modelId);
      const ws = modelInfo?.data_source?.ts_worksheet_id;
      if (ws) navigate(`/mkg/thoughtspot/${ws}`);
    },
    [navigate]
  );

  const handleDeleteModel = async (id: string) => {
    const model = modelList.find(m => m.id === id);
    const numMetrics = (model?.stats as DataModelStats | undefined)?.num_mapped_metrics ?? 0;

    if (numMetrics > 0) {
      showToast(
        "Cannot delete model",
        `Please delete the ${numMetrics} metric${numMetrics > 1 ? "s" : ""} linked to this model first.`,
        "error"
      );
      return;
    }

    await deleteModel(id);
    setModelList(prev => prev.filter(m => m.id !== id));
  };

  return (
    <ModelList
      listModel={modelList}
      handleOpenModelEditor={handleOpenModelEditor}
      onOpenThoughtspotFormula={handleOpenThoughtspotFormula}
      onDeleteModel={handleDeleteModel}
      statsLoading={statsLoading}
    />
  );
};

export default ModelListContainer;
